import axios from 'axios';

// API configuration
const API_VERSION = '/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_VERSION,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface BPReading {
  id?: number;
  systolic: number;
  diastolic: number;
  heart_rate?: number;
  notes?: string;
  timestamp?: string;
}

export interface BPTrend {
  readings: BPReading[];
  avg_systolic: number;
  avg_diastolic: number;
  trend_direction: 'improving' | 'stable' | 'concerning' | 'no_data' | 'insufficient_data';
}

export interface ChatMessage {
  message: string;
  session_id?: string;
  user_id?: string;
}

export type FeedbackValue = 'positive' | 'negative';

export interface ChatFeedbackRequest {
  value: FeedbackValue;
  reason_code?: string;
  comment?: string;
}

export interface ChatFeedbackResponse {
  response_id: string;
  value: FeedbackValue;
  reason_code?: string | null;
  comment?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListItem {
  response_id: string;
  session_id: string;
  user_id?: string | null;
  model: string;
  user_message: string;
  assistant_response: string;
  value: FeedbackValue;
  reason_code?: string | null;
  comment?: string | null;
  langfuse_trace_id?: string | null;
  response_created_at: string;
  feedback_created_at: string;
  feedback_updated_at: string;
}

export interface FeedbackListResponse {
  items: FeedbackListItem[];
  total: number;
  limit: number;
  value: 'all' | FeedbackValue;
}

export interface FeedbackStats {
  total: number;
  positive: number;
  negative: number;
  with_notes: number;
}

export interface FeedbackLoginRequest {
  username: string;
  password: string;
}

export interface FeedbackLoginResponse {
  authenticated: boolean;
}

export interface Citation {
  url: string;
  title: string;
  authority: string;
  domain: string;
}

export interface ChatResponse {
  response: string;
  session_id: string;
  timestamp: string;
  response_id?: string | null;
  metadata?: {
    sources?: Citation[];
    [key: string]: any;
  };
}

// API functions
export const apiService = {
  // Blood Pressure API
  async createBPReading(reading: Omit<BPReading, 'id' | 'timestamp'>, userId = 'default'): Promise<BPReading> {
    const response = await api.post(`/bp?user_id=${userId}`, reading);
    return response.data;
  },

  async getBPReadings(userId = 'default', limit = 50, days?: number): Promise<BPReading[]> {
    const params = new URLSearchParams({ 
      user_id: userId, 
      limit: limit.toString() 
    });
    if (days) params.append('days', days.toString());
    
    const response = await api.get(`/bp?${params}`);
    return response.data;
  },

  async getBPTrend(userId = 'default', days = 30): Promise<BPTrend> {
    const response = await api.get(`/bp/trend/analysis?user_id=${userId}&days=${days}`);
    return response.data;
  },

  async deleteBPReading(id: number, userId = 'default'): Promise<void> {
    await api.delete(`/bp/${id}?user_id=${userId}`);
  },

  // Chat API
  async sendChatMessage(message: ChatMessage): Promise<ChatResponse> {
    const response = await api.post('/chat', message);
    return response.data;
  },

  async getWelcomeMessage(): Promise<{ message: string; timestamp: string }> {
    const response = await api.post('/chat/welcome');
    return response.data;
  },

  async getChatSession(sessionId: string): Promise<any> {
    const response = await api.get(`/chat/session/${sessionId}`);
    return response.data;
  },

  async deleteChatSession(sessionId: string): Promise<void> {
    await api.delete(`/chat/session/${sessionId}`);
  },

  async submitChatFeedback(responseId: string, payload: ChatFeedbackRequest): Promise<ChatFeedbackResponse> {
    const response = await api.put(`/chat/feedback/${responseId}`, payload);
    return response.data;
  },

  async deleteChatFeedback(responseId: string): Promise<{ removed: boolean }> {
    const response = await api.delete(`/chat/feedback/${responseId}`);
    return response.data;
  },

  async loginFeedbackAdmin(payload: FeedbackLoginRequest): Promise<FeedbackLoginResponse> {
    const response = await api.post('/chat/feedback/login', payload);
    return response.data;
  },

  async logoutFeedbackAdmin(): Promise<FeedbackLoginResponse> {
    const response = await api.post('/chat/feedback/logout');
    return response.data;
  },

  async getChatFeedback(value: 'all' | FeedbackValue = 'all', limit = 50): Promise<FeedbackListResponse> {
    const response = await api.get(`/chat/feedback?value=${value}&limit=${limit}`);
    return response.data;
  },

  async getChatFeedbackStats(): Promise<FeedbackStats> {
    const response = await api.get('/chat/feedback/stats');
    return response.data;
  },

  // Health check
  async healthCheck(): Promise<{ status: string; version: string }> {
    const response = await api.get('/health', { baseURL: '' });
    return response.data;
  }
};

// Error handler
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    throw error;
  }
);
